/**
 * Ops map road-graph polish — CARTO Dark Matter vector + optional Esri road ink.
 * Soft≠Live: map ink for operator navigation; never invents Live geometry.
 *
 * Stock Dark Matter paints path/service/minor in near-black and hides them until
 * high zoom. We (1) retune those layers, (2) add *extra* vector overlays, and
 * (3) fade in Esri World Transportation / Places reference rasters for street
 * detail past native CARTO overzoom — still free-roam, no Soft invent Soft pins.
 */

/** Esri reference XYZ (public World_* · no token). Soft≠Live: basemap only. */
const ESRI_WORLD_TRANSPORT =
  "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}";
const ESRI_WORLD_PLACES =
  "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}";

const FONT = [
  "Montserrat Regular",
  "Open Sans Regular",
  "Noto Sans Regular",
  "HanWangHeiLight Regular",
  "NanumBarunGothic Regular",
];

/** @param {import("maplibre-gl").Map} m */
export function opsCartoSourceId(m) {
  if (!m || typeof m.getStyle !== "function") return null;
  const sources = m.getStyle().sources || {};
  if (sources.carto && sources.carto.type === "vector") return "carto";
  const hit = Object.entries(sources).find(
    ([, s]) => s && s.type === "vector",
  );
  return hit ? hit[0] : null;
}

/**
 * @param {import("maplibre-gl").Map} m
 * @param {string} id
 * @param {number} minz
 * @param {number} [maxz]
 */
function zoom(m, id, minz, maxz = 24) {
  if (!m.getLayer(id)) return false;
  try {
    m.setLayerZoomRange(id, minz, maxz);
    return true;
  } catch {
    return false;
  }
}

/**
 * @param {import("maplibre-gl").Map} m
 * @param {string} id
 * @param {Record<string, unknown>} paint
 */
function paint(m, id, paintProps) {
  if (!m.getLayer(id)) return 0;
  let n = 0;
  for (const [k, v] of Object.entries(paintProps)) {
    try {
      m.setPaintProperty(id, k, v);
      n += 1;
    } catch {
      /* skip bad key */
    }
  }
  return n;
}

/**
 * First symbol layer id — insert road overlays just under labels.
 * @param {import("maplibre-gl").Map} m
 */
function beforeLabels(m) {
  const layers = m.getStyle()?.layers || [];
  const sym = layers.find((l) => l.type === "symbol");
  return sym ? sym.id : undefined;
}

/**
 * @param {import("maplibre-gl").Map} m
 * @returns {{ ok: boolean, src: string|null, retuned: number, overlays: string[], err?: string }}
 */
export function enrichOpsRoadGraph(m) {
  const report = { ok: false, src: null, retuned: 0, overlays: [] };
  if (!m || typeof m.getLayer !== "function") {
    report.err = "no-map";
    return report;
  }
  const src = opsCartoSourceId(m);
  report.src = src;
  if (!src) {
    report.err = "no-vector-source";
    return report;
  }

  /* ── 1) Retune stock Dark Matter layers (best-effort) ─────────────── */
  for (const id of ["road_path", "bridge_path", "tunnel_path"]) {
    if (zoom(m, id, 12)) report.retuned += 1;
  }
  for (const id of [
    "road_service_case",
    "road_service_fill",
    "bridge_service_case",
    "bridge_service_fill",
    "tunnel_service_case",
    "tunnel_service_fill",
  ]) {
    if (zoom(m, id, 14)) report.retuned += 1;
  }
  for (const id of [
    "road_minor_fill",
    "bridge_minor_fill",
    "tunnel_minor_fill",
    "road_minor_case",
    "bridge_minor_case",
    "tunnel_minor_case",
  ]) {
    if (zoom(m, id, 14)) report.retuned += 1;
  }
  for (const [id, minz] of [
    ["roadname_minor", 14.2],
    ["roadname_sec", 13],
    ["roadname_pri", 11],
    ["roadname_major", 9.5],
  ]) {
    if (zoom(m, id, minz)) report.retuned += 1;
  }

  const cases = (m.getStyle()?.layers || []).filter(
    (layer) => layer.type === "line" && /_case/.test(layer.id),
  );
  for (const layer of cases) {
    report.retuned += paint(m, layer.id, { "line-color": "#100E0C" });
  }
  for (const id of [
    "road_mot_fill_noramp",
    "road_mot_fill_ramp",
    "bridge_mot_fill",
    "tunnel_mot_fill",
    "road_trunk_fill_noramp",
    "road_trunk_fill_ramp",
    "bridge_trunk_fill",
    "tunnel_trunk_fill",
  ]) {
    report.retuned += paint(m, id, { "line-color": "#C4A36A" });
  }
  for (const id of ["rail", "rail_dash", "tunnel_rail", "tunnel_rail_dash"]) {
    report.retuned += paint(m, id, { "line-color": "#6E6254" });
  }

  for (const id of ["road_path", "bridge_path", "tunnel_path"]) {
    report.retuned += paint(m, id, {
      "line-color": "#A8B5A0",
      "line-opacity": 0.95,
      "line-dasharray": [1.1, 0.75],
      "line-width": [
        "interpolate",
        ["linear"],
        ["zoom"],
        12,
        0.9,
        15,
        1.8,
        17,
        3,
        19,
        4.5,
      ],
    });
  }
  for (const id of [
    "road_service_fill",
    "bridge_service_fill",
    "tunnel_service_fill",
  ]) {
    report.retuned += paint(m, id, {
      "line-color": "#6A6258",
      "line-opacity": 1,
      "line-width": [
        "interpolate",
        ["linear"],
        ["zoom"],
        12,
        1.4,
        15,
        2.6,
        17,
        5,
        19,
        8,
      ],
    });
  }
  for (const id of ["road_minor_fill", "bridge_minor_fill", "tunnel_minor_fill"]) {
    report.retuned += paint(m, id, {
      "line-color": "#8F8474",
      "line-opacity": 1,
      "line-width": [
        "interpolate",
        ["linear"],
        ["zoom"],
        13,
        2,
        15,
        3.8,
        17,
        8,
        19,
        13,
      ],
    });
  }
  for (const id of [
    "road_sec_fill_noramp",
    "bridge_sec_fill",
    "tunnel_sec_fill",
  ]) {
    report.retuned += paint(m, id, { "line-color": "#B7A890" });
  }
  for (const id of [
    "road_pri_fill_noramp",
    "bridge_pri_fill",
    "tunnel_pri_fill",
  ]) {
    report.retuned += paint(m, id, { "line-color": "#D4C4A8" });
  }

  /* One ribbon per class. Overlays carry the mid zoom, then fade by 14.4
     so the cased stock line is the street, not two strokes on top of each other.
     Bridges sit one step lighter. Tunnels are dashed and quieter. */
  const zWidth = (pairs) => ["interpolate", ["linear"], ["zoom"], ...pairs];
  const ribbonKind = (id) => {
    if (/path/.test(id)) return "path";
    if (/service/.test(id)) return "service";
    if (/minor/.test(id)) return "minor";
    if (/sec/.test(id)) return "sec";
    if (/pri/.test(id)) return "pri";
    if (/mot|trunk/.test(id)) return "spine";
    if (/rail/.test(id)) return "rail";
    return "";
  };
  const bodyColor = {
    spine: "#C4A36A", pri: "#D4C4A8", sec: "#B7A890", minor: "#8F8474",
    service: "#6A6258", path: "#A8B5A0", rail: "#6E6254",
  };
  const bridgeColor = {
    spine: "#E4C48A", pri: "#E7D7B8", sec: "#D4C4A8", minor: "#C4B49A",
    service: "#8A8175", path: "#C5D2BE", rail: "#8A7B66",
  };
  const bodyWidth = {
    spine: [6, 0.8, 10, 1.6, 13, 2.8, 16, 5, 19, 8],
    pri: [8, 0.6, 12, 1.6, 15, 3, 18, 5.5],
    sec: [11, 0.5, 14, 1.5, 16, 2.6, 19, 4.8],
    minor: [14, 1.1, 16, 2, 18, 3.6, 19, 5],
    service: [14, 0.8, 16, 1.4, 18, 2.4, 19, 3.4],
    path: [12, 0.7, 15, 1.3, 18, 2.4, 19, 3.4],
    rail: [13, 0.6, 16, 1.3, 19, 2.2],
  };
  const caseWidth = {
    spine: [6, 1.6, 10, 2.8, 13, 4.4, 16, 7, 19, 11],
    pri: [8, 1.3, 12, 2.6, 15, 4.4, 18, 7.5],
    sec: [11, 1.1, 14, 2.4, 16, 4, 19, 6.6],
    minor: [14, 1.8, 16, 3, 18, 5, 19, 6.8],
    service: [14, 1.3, 16, 2.1, 18, 3.4, 19, 4.6],
    path: [12, 1.4, 15, 2.2, 18, 3.6, 19, 4.8],
    rail: [13, 0.6, 16, 1.3, 19, 2.2],
  };
  for (const layer of m.getStyle()?.layers || []) {
    if (layer.type !== "line" || !/^(road|bridge|tunnel)_|^rail/.test(layer.id)) continue;
    const kind = ribbonKind(layer.id);
    if (!kind) continue;
    const tunnel = layer.id.startsWith("tunnel_");
    const bridge = layer.id.startsWith("bridge_");
    const isCase = /_case/.test(layer.id);
    const props = {
      "line-width": zWidth(isCase ? caseWidth[kind] : bodyWidth[kind]),
    };
    if (isCase) props["line-color"] = "#100E0C";
    else props["line-color"] = (bridge && bridgeColor[kind]) || bodyColor[kind];
    if (tunnel) props["line-opacity"] = isCase ? 0.35 : 0.42;
    else if (!isCase) props["line-opacity"] = 1;
    if (tunnel && !isCase && kind !== "rail") props["line-dasharray"] = [1.6, 0.9];
    report.retuned += paint(m, layer.id, props);
  }

  /* ── 2) Additive overlays — mid-zoom ink, gone by the time stock casings lead ─ */
  const before = beforeLabels(m);
  const handOff = () => ["interpolate", ["linear"], ["zoom"], 10.5, 0.95, 12.8, 0.62, 14.4, 0];

  /**
   * @param {string} id
   * @param {object} layer
   */
  function putOverlay(id, layer) {
    if (m.getLayer(id)) {
      try {
        m.removeLayer(id);
      } catch {
        /* */
      }
    }
    try {
      if (before) m.addLayer({ id, ...layer }, before);
      else m.addLayer({ id, ...layer });
      report.overlays.push(id);
    } catch (e) {
      report.err = String((e && e.message) || e || "addLayer");
    }
  }

  /* Dark edge under the path, then the sage stroke (path / track / foot). */
  putOverlay("ops-path-glow", {
    type: "line",
    source: src,
    "source-layer": "transportation",
    minzoom: 11,
    filter: [
      "all",
      [
        "match",
        ["geometry-type"],
        ["LineString", "MultiLineString"],
        true,
        false,
      ],
      [
        "match",
        ["get", "class"],
        ["path", "track", "pedestrian", "footway", "cycleway", "bridleway"],
        true,
        false,
      ],
    ],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": "#100E0C",
      "line-opacity": handOff(),
      "line-width": [
        "interpolate",
        ["linear"],
        ["zoom"],
        11,
        2.2,
        16,
        5,
        19,
        9,
      ],
      "line-blur": 0.15,
    },
  });

  putOverlay("ops-path", {
    type: "line",
    source: src,
    "source-layer": "transportation",
    minzoom: 11,
    filter: [
      "all",
      [
        "match",
        ["geometry-type"],
        ["LineString", "MultiLineString"],
        true,
        false,
      ],
      [
        "match",
        ["get", "class"],
        ["path", "track", "pedestrian", "footway", "cycleway", "bridleway"],
        true,
        false,
      ],
    ],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": "#C5D2BE",
      "line-opacity": handOff(),
      "line-dasharray": [1.25, 0.7],
      "line-width": [
        "interpolate",
        ["linear"],
        ["zoom"],
        11,
        0.8,
        15,
        1.7,
        17,
        2.8,
        19,
        4.2,
      ],
    },
  });

  /* Service / driveway net — solid cool graphite, earlier than stock. */
  putOverlay("ops-service", {
    type: "line",
    source: src,
    "source-layer": "transportation",
    minzoom: 12,
    filter: [
      "all",
      [
        "match",
        ["geometry-type"],
        ["LineString", "MultiLineString"],
        true,
        false,
      ],
      ["match", ["get", "class"], ["service", "track"], true, false],
    ],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": "#8A8175",
      "line-opacity": handOff(),
      "line-width": [
        "interpolate",
        ["linear"],
        ["zoom"],
        12,
        1,
        15,
        2.2,
        17,
        4,
        19,
        6.5,
      ],
    },
  });

  /* Minor streets — light rim so block nets read. */
  putOverlay("ops-minor", {
    type: "line",
    source: src,
    "source-layer": "transportation",
    minzoom: 12.5,
    filter: [
      "all",
      [
        "match",
        ["geometry-type"],
        ["LineString", "MultiLineString"],
        true,
        false,
      ],
      [
        "match",
        ["get", "class"],
        ["minor", "street", "street_limited", "tertiary"],
        true,
        false,
      ],
    ],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": "#C4B49A",
      "line-opacity": handOff(),
      "line-width": [
        "interpolate",
        ["linear"],
        ["zoom"],
        12.5,
        1.1,
        15,
        2.4,
        17,
        4.5,
        19,
        7,
      ],
    },
  });

  /* Secondary / tertiary arteries — earlier hierarchy for multi-block read. */
  putOverlay("ops-secondary", {
    type: "line",
    source: src,
    "source-layer": "transportation",
    minzoom: 11.5,
    filter: [
      "all",
      [
        "match",
        ["geometry-type"],
        ["LineString", "MultiLineString"],
        true,
        false,
      ],
      [
        "match",
        ["get", "class"],
        ["secondary", "tertiary"],
        true,
        false,
      ],
    ],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": "#E0D2BC",
      "line-opacity": handOff(),
      "line-width": [
        "interpolate",
        ["linear"],
        ["zoom"],
        11.5,
        1.4,
        14,
        2.8,
        17,
        5.5,
        19,
        9,
      ],
    },
  });

  /* Motorway and trunk, separate from primary so a class pick can hide one. */
  putOverlay("ops-motorway", {
    type: "line",
    source: src,
    "source-layer": "transportation",
    minzoom: 8,
    filter: [
      "all",
      ["match", ["geometry-type"], ["LineString", "MultiLineString"], true, false],
      ["match", ["get", "class"], ["motorway", "trunk"], true, false],
    ],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": "#E4C48A",
      "line-opacity": handOff(),
      "line-width": ["interpolate", ["linear"], ["zoom"], 8, 1.1, 12, 2.4, 14, 3.6],
    },
  });

  /* Primary — bright spine for city-scale navigation. */
  putOverlay("ops-primary", {
    type: "line",
    source: src,
    "source-layer": "transportation",
    minzoom: 10.5,
    filter: [
      "all",
      [
        "match",
        ["geometry-type"],
        ["LineString", "MultiLineString"],
        true,
        false,
      ],
      [
        "match",
        ["get", "class"],
        ["primary"],
        true,
        false,
      ],
    ],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": "#E7D7B8",
      "line-opacity": handOff(),
      "line-width": [
        "interpolate",
        ["linear"],
        ["zoom"],
        10.5,
        1.6,
        13,
        3.2,
        16,
        6.5,
        19,
        12,
      ],
    },
  });

  /* Oneway — chevron › (OSM oneway=1 / -1). Missing mark ≈ two-way. */
  const onewayLayout = {
    "symbol-placement": "line",
    "symbol-spacing": 48,
    "text-field": "›",
    "text-font": FONT,
    "text-size": [
      "interpolate",
      ["linear"],
      ["zoom"],
      14.5,
      13,
      17,
      16,
      19,
      18,
    ],
    "text-keep-upright": false,
    "text-rotation-alignment": "map",
    "text-pitch-alignment": "viewport",
    "text-allow-overlap": false,
    "text-ignore-placement": false,
    "text-padding": 2,
  };
  const onewayPaint = {
    "text-color": "#D7E3D4",
    "text-halo-color": "#100E0C",
    "text-halo-width": 1.8,
    "text-opacity": 1,
  };

  putOverlay("ops-oneway", {
    type: "symbol",
    source: src,
    "source-layer": "transportation",
    minzoom: 14.5,
    filter: [
      "any",
      ["==", ["get", "oneway"], 1],
      ["==", ["to-string", ["get", "oneway"]], "1"],
      ["==", ["get", "oneway"], true],
    ],
    layout: onewayLayout,
    paint: onewayPaint,
  });

  putOverlay("ops-oneway-opp", {
    type: "symbol",
    source: src,
    "source-layer": "transportation",
    minzoom: 14.5,
    filter: [
      "any",
      ["==", ["get", "oneway"], -1],
      ["==", ["to-string", ["get", "oneway"]], "-1"],
    ],
    layout: { ...onewayLayout, "text-rotate": 180 },
    paint: onewayPaint,
  });

  report.ok = report.overlays.length >= 3;
  try {
    enrichOpsRoadReference(m, report);
  } catch (e) {
    report.ref_err = String((e && e.message) || e);
  }
  try {
    if (typeof window !== "undefined") {
      window.__opsRoadEnrich = report;
    }
  } catch {
    /* */
  }
  return report;
}

/**
 * High-zoom Esri road / place ink under labels — fills CARTO native z14 overzoom.
 * Soft≠Live: reference rasters only; opacity ramps so world overview stays light.
 * @param {import("maplibre-gl").Map} m
 * @param {{ overlays?: string[], ref?: string[] }} report
 */
export function enrichOpsRoadReference(m, report) {
  if (!m || typeof m.addSource !== "function") return;
  const before = beforeLabels(m);
  const ref = [];
  function putRaster(id, tiles, opacityStops) {
    try {
      if (!m.getSource(id)) {
        m.addSource(id, {
          type: "raster",
          tiles,
          tileSize: 256,
          maxzoom: 19,
          attribution: "Esri",
        });
      }
      if (m.getLayer(id)) {
        try {
          m.removeLayer(id);
        } catch {
          /* */
        }
      }
      const layer = {
        id,
        type: "raster",
        source: id,
        minzoom: 15.2,
        paint: {
          "raster-opacity": [
            "interpolate",
            ["linear"],
            ["zoom"],
            ...opacityStops,
          ],
          "raster-saturation": -0.45,
          "raster-contrast": 0.22,
          "raster-brightness-min": 0,
          "raster-brightness-max": 0.62,
        },
      };
      if (before) m.addLayer(layer, before);
      else m.addLayer(layer);
      ref.push(id);
      if (report && Array.isArray(report.overlays)) report.overlays.push(id);
    } catch {
      /* */
    }
  }
  /* Cool night-friendly road network · fade in only when streets matter. */
  putRaster("ops-esri-transport", [ESRI_WORLD_TRANSPORT], [
    15.2, 0, 16.6, 0.1, 19, 0.18,
  ]);
  putRaster("ops-esri-places", [ESRI_WORLD_PLACES], [
    16, 0, 17.5, 0.1, 19, 0.16,
  ]);
  if (report) report.ref = ref;
}

/**
 * Apply now + once after idle (tiles/source fully ready).
 * @param {import("maplibre-gl").Map} m
 */
export function installOpsRoadGraph(m) {
  if (!m) return;
  const run = () => {
    try {
      return enrichOpsRoadGraph(m);
    } catch (e) {
      const err = { ok: false, err: String((e && e.message) || e) };
      try {
        if (typeof window !== "undefined") window.__opsRoadEnrich = err;
      } catch {
        /* */
      }
      return err;
    }
  };
  run();
  try {
    m.once("idle", () => {
      run();
    });
  } catch {
    /* */
  }
}

/** @returns {number} */
export function opsMapPixelRatio() {
  try {
    const dpr =
      typeof window !== "undefined" ? Number(window.devicePixelRatio) : 1;
    if (!Number.isFinite(dpr) || dpr <= 0) return 1;
    return Math.min(2, Math.max(1, dpr));
  } catch {
    return 1;
  }
}
